import { useCallback, useEffect, useState } from "react";
import useSearchFilter from "@hooks/useSearchFilter";
import SearchChipExpand from "@features/list-search/search-chip-expand";
import MetadataFieldEditMode from "@features/metadata-fields/metadata-field-edit-mode";
import { Divider, Grid, TextField, Typography } from "@mzc-pdc/ui";
import { ChipOwnersInput, ChipUsersInputV2 } from "@features/chip-popper-components/chip-users-input";
import TagsInput from "@features/tags-input/tags-input";
import SearchAttributionsInput from "@features/attributions-input/search-attributions-input";
import { Autocomplete } from "@components_v2/autocomplete";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AutocompleteWithObject } from "@components_v2/autocomplete/autocomplete-with-object";
import { IconReset, ImageEmptySearchMini } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { StyledAutocompletePopper } from "@features/chip-popper-components/chip-types-input/chip-types-input.styled";
import { BasicDatePicker } from "@features/datepicker/datepicker";
import dayjs from "dayjs";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { convertToUTCDateTime } from "@utils/convertToUTCDateTime";
import ProjectsInput from "@features/projects-input/projects-input";
import SelectCategoryGroupPopper from "@features/categories-input/select-category-group-popper";
import ProjectSingleInput from "@features/projects-input/project-single-input";

const SearchFilters = ({
    domain,
    type,
    values,
    optionValues,
    onClickSubmit,
    onClickReset,
    isSearchEngine,
    scope,
    hiddenParams,
    currentCategoryGroup,
}) => {
    const { t } = useTranslation();
    const stage = useSelector((state) => state.stage);
    const assets = useSelector((state) => state.assets);
    const project = useSelector((state) => state.project);

    const { parseSearchParamsToSearchValue } = useSearchFilter(domain, "list", type, isSearchEngine, stage.version);
    const [decodedSearchParams, setDecodedSearchParams] = useState([]); //type마다 다른 검색 key를 가지는데 일일히 다 상태로 만들기는 너무 많아서 객체로 관리하고자 생성
    const [editTempValue, setEditTempValue] = useState({});
    const [validationErrors, setValidationErrors] = useState({});
    const MAX_CONTENT_FIELDS = 5;

    const getChipIsChanged = useCallback(
        (item) => {
            const filterOfDateType = decodedSearchParams.find(
                (el) => el.type === "assetDateRange" && item.id === el.id,
            );
            if (filterOfDateType) {
                return values.startAt === editTempValue.startAt && values.endAt === editTempValue.endAt;
            } else return values[item.id] === editTempValue[item.id];
        },
        [decodedSearchParams, editTempValue, values],
    );

    const getValidationErrors = useCallback(
        (item) => {
            const filterOfDateType = decodedSearchParams.find(
                (el) => el.type === "assetDateRange" && item.id === el.id,
            );
            if (filterOfDateType) {
                return validationErrors.startAt || validationErrors.endAt;
            } else return !!validationErrors[item.id];
        },
        [validationErrors, decodedSearchParams],
    );

    const onHandleChangeValue = useCallback(
        (id, value) => {
            setEditTempValue((prevTempValue) => ({ ...prevTempValue, [id]: value }));
        },
        [values, editTempValue],
    );

    const onHandleCancel = useCallback(
        (item) => {
            if (item.type === "assetDateRange") {
                setEditTempValue({ ...editTempValue, startAt: values.startAt, endAt: values.endAt });
            } else {
                setEditTempValue({ ...editTempValue, [item.id]: values[item.id] });
            }
        },
        [values, editTempValue],
    );

    const onHandleDeleteChip = useCallback(
        ({ id, type }) => {
            // date Type
            const filterOfDateType = decodedSearchParams.find((el) => el.type === "assetDateRange" && id === el.id);
            const filterOfUserType = decodedSearchParams.find((el) => ["users"].includes(el.type) && id === el.id);
            const filterOfCategoriesType = decodedSearchParams.find((el) => el.type === "categories" && id === el.id);
            if (filterOfDateType) {
                onClickSubmit({
                    startAt: null,
                    endAt: null,
                });
            } else if (filterOfUserType) onClickSubmit({ [id]: undefined, [`${id}Name`]: undefined });
            else if (filterOfCategoriesType) {
                onClickSubmit({
                    [id]: undefined,
                    categoryGroup: { id: undefined },
                });
            } else if (id === "contentSchema") onClickSubmit({ contentSchema: undefined });
            else onClickSubmit({ [id]: undefined });
        },
        [editTempValue, decodedSearchParams],
    );

    const onHandleValidationError = (id, error) => {
        setValidationErrors({ ...validationErrors, [id]: error });
    };

    const getModifiedOptions = useCallback((options, value) => {
        if (!options) return [];
        return options?.map((option) => ({
            ...option,
            disabled:
                value?.length >= MAX_CONTENT_FIELDS &&
                !value?.find((selectedOption) => selectedOption.value === option.value),
        }));
    }, []);

    const PopperComponent = useCallback((props) => {
        const { disablePortal: _disablePortal, anchorEl: _anchorEl, open: _open, ...other } = props;
        return <StyledAutocompletePopper {...other}></StyledAutocompletePopper>;
    }, []);

    const getValueByParam = useCallback(
        (values, param) => {
            const { id, value, options, type, multiple, placeholder, isCustomField } = param;
            if (!type) return;

            if (isCustomField)
                return (
                    <MetadataFieldEditMode
                        id={id}
                        type={type}
                        value={values[id]}
                        options={options?.map((option) => ({
                            ...option,
                            selected: values[id]?.includes(option.value),
                        }))}
                        onChangeField={(id, value) => {
                            onHandleChangeValue(id, value);
                        }}
                    />
                );

            if (type === "text")
                return (
                    <TextField
                        sx={{ width: "100%" }}
                        value={values[id]}
                        placeholder={"이름 입력"}
                        onChange={(e) => onHandleChangeValue(id, e.target.value)}
                    ></TextField>
                );

            if (type === "users")
                return (
                    <ChipUsersInputV2
                        user={{ id: values[id], name: values[`${id}Name`] }}
                        placeholder={placeholder}
                        onDeleteUser={() => {
                            onHandleChangeValue(`${id}Name`, null);
                            onHandleChangeValue(id, null);
                        }}
                        onAddUser={(user) => {
                            onHandleChangeValue(`${id}Name`, user.name);
                            onHandleChangeValue(id, user.id);
                        }}
                        isEdit={false}
                        scope={scope}
                        isPopperComponent={true}
                    />
                );
            if (type === "owners")
                return (
                    <ChipOwnersInput
                        user={values.owner || {}}
                        placeholder={placeholder}
                        onDeleteUser={() => {
                            onHandleChangeValue(`owner`, {});
                        }}
                        onAddUser={(user) => {
                            onHandleChangeValue(`owner`, { id: user.id, name: user.name });
                        }}
                        isEdit={false}
                        isPopperComponent={true}
                    />
                );
            // asset 목록에서 startAt, endAt을 하나의 객체로 관리하지 않고 각각 처리하였기에 에셋 전용 레거시로 남겨둠
            if (type === "assetDateRange")
                return (
                    <Grid
                        container
                        flexDirection={"row"}
                        gap={1}
                        sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
                    >
                        <BasicDatePicker
                            sx={{ width: "49%" }}
                            label={t(`common::label::Start`, `Start`)}
                            value={dayjs(values.startAt).year() <= 1970 ? null : values.startAt}
                            maxDate={editTempValue?.endAt}
                            onChangeValue={(v) => {
                                onHandleChangeValue("startAt", convertToUTCDateTime(v, "start", true));
                            }}
                        ></BasicDatePicker>
                        <BasicDatePicker
                            sx={{ width: "49%" }}
                            label={t(`common::label::End`, `End`)}
                            value={dayjs(values.endAt).year() >= 9999 ? null : values.endAt}
                            minDate={editTempValue?.startAt}
                            onChangeValue={(v) => {
                                onHandleChangeValue("endAt", convertToUTCDateTime(v, "end", true));
                            }}
                            onError={(error) => onHandleValidationError("endAt", error)}
                        ></BasicDatePicker>
                    </Grid>
                );
            if (type === "dateRange")
                return (
                    <Grid
                        container
                        flexDirection={"row"}
                        gap={1}
                        sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
                    >
                        <BasicDatePicker
                            sx={{ width: "49%" }}
                            label={t(`common::label::Start`, `Start`)}
                            value={value?.startAt}
                            maxDate={value?.endAt}
                            onChangeValue={(v) => {
                                onHandleChangeValue(id, {
                                    startAt: `${v?.format("YYYY-MM-DD")} 00:00:00`,
                                    endAt: `${dayjs(value?.endAt ?? "9999-99-99").format("YYYY-MM-DD")} 23:59:59`,
                                });
                            }}
                        ></BasicDatePicker>
                        <BasicDatePicker
                            sx={{ width: "49%" }}
                            label={t(`common::label::End`, `End`)}
                            value={value?.endAt}
                            minDate={value?.startAt}
                            onChangeValue={(v) => {
                                onHandleChangeValue(id, {
                                    startAt: `${dayjs(value?.startAt ?? "0000-00-00").format("YYYY-MM-DD")} 00:00:00`,
                                    endAt: `${v?.format("YYYY-MM-DD")} 23:59:59`,
                                });
                            }}
                        ></BasicDatePicker>
                    </Grid>
                );
            if (type === "tags")
                return (
                    <TagsInput
                        tags={values[id]?.map((v) => ({ value: v }))}
                        onDeleteTag={(index) => {
                            onHandleChangeValue(
                                id,
                                values[id]?.filter((_, idx) => idx !== index),
                            );
                        }}
                        onAddTag={(tag) => {
                            onHandleChangeValue(id, values[id] ? values[id].concat(tag?.value) : [tag?.value]);
                        }}
                        isEdit={false}
                        maxCount={MAX_CONTENT_FIELDS}
                    />
                );
            if (type === "attributions") {
                return (
                    <SearchAttributionsInput
                        defaultValues={values[id]}
                        // onChangeAttributions={(attributions, validationErrors) => {
                        //     if (!Object.keys(validationErrors)?.length)
                        //         onHandleChangeValue(
                        //             "attributions",
                        //             attributions.map(({ key, value }) => ({ key, value })),
                        //         );
                        // }}
                    />
                );
            }
            if (type === "categories") {
                return (
                    <SelectCategoryGroupPopper
                        disablePopper={true}
                        isMultiple={true}
                        type={"Asset"}
                        defaultCategoryGroup={{
                            id: currentCategoryGroup?.value,
                        }}
                        project={project}
                        selectedCategories={values[id]?.map((c) => {
                            return {
                                label: c.name,
                                name: c.name,
                                value: c.id,
                                id: c.id,
                            };
                        })}
                        onChangeCategories={(value) => {
                            onHandleChangeValue(
                                id,
                                value.categories?.map((option) => ({ id: option.id, name: option.name })),
                            );
                            onHandleChangeValue("categoryGroup", {
                                id: value.categoryGroup.id,
                                name: value.categoryGroup.label,
                            });
                        }}
                        maxCount={MAX_CONTENT_FIELDS}
                    />
                );
            }
            if (type === "select") {
                const defaultSelectValue = multiple ? [] : null;

                return (
                    <Autocomplete
                        open={true}
                        id={id}
                        multiple={multiple}
                        value={values[id] ?? defaultSelectValue}
                        options={optionValues?.[id]?.filter(({ value }) => value) ?? []}
                        placeholder={placeholder}
                        onClickOption={(id, value) => onHandleChangeValue(id, value)}
                        PopperComponent={PopperComponent}
                        getOptionDisabled={(option) =>
                            values[id]?.length >= MAX_CONTENT_FIELDS && !values[id]?.includes(option)
                        }
                        noOptionsText={
                            <Grid
                                container
                                justifyContent={"center"}
                                alignItems={`center`}
                                flexDirection={"column"}
                                sx={{ height: "200px" }}
                            >
                                <ImageEmptySearchMini width={24} height={24} />
                                <Typography component={"span"} color={theme.palette.secondary[800]}>
                                    {t(`common::msg::No search results found.`)}
                                </Typography>
                            </Grid>
                        }
                        sx={{ ".MuiAutocomplete-endAdornment": { visibility: "hidden" } }}
                    />
                );
            } else if (type === "objectSelect") {
                const defaultSelectValue = multiple ? [] : null;
                const _values = multiple ? values[id]?.map((v) => ({ ...v, label: v.name, value: v.id })) : values[id];
                return (
                    <AutocompleteWithObject
                        open={true}
                        id={id}
                        multiple={multiple}
                        value={_values ?? defaultSelectValue}
                        options={
                            multiple
                                ? getModifiedOptions(optionValues?.[id], _values)
                                : optionValues?.[id]?.filter(({ value }) => value) ?? []
                        }
                        placeholder={placeholder}
                        onClickOption={(id, value) => onHandleChangeValue(id, value)}
                        PopperComponent={PopperComponent}
                        getOptionDisabled={(option) =>
                            multiple ? _values?.length >= MAX_CONTENT_FIELDS && !_values?.includes(option) : false
                        }
                        noOptionsText={
                            <Grid
                                container
                                justifyContent={"center"}
                                alignItems={`center`}
                                flexDirection={"column"}
                                sx={{ height: "85px" }}
                            >
                                <Typography component={"span"} color={theme.palette.secondary[800]}>
                                    {t(`common::msg::No search results found.`)}
                                </Typography>
                            </Grid>
                        }
                        sx={{ ".MuiAutocomplete-endAdornment": { visibility: "hidden" } }}
                    />
                );
            } else if (type === "project") {
                return (
                    <ProjectsInput
                        data={values[id] ?? []}
                        isClear={!values[id]}
                        disabled={false}
                        placeholder={
                            (!values[id] || values[id]?.length === 0) &&
                            t(`common::label::All projects`, `All projects`)
                        }
                        isPopperComponent={true}
                        onChange={(projects) => {
                            onHandleChangeValue(
                                id,
                                projects.map((option) => ({ id: option.id, name: option.name })),
                            );
                        }}
                        noOptionsText={
                            <Grid
                                container
                                justifyContent={"center"}
                                alignItems={`center`}
                                flexDirection={"column"}
                                sx={{ height: "85px" }}
                            >
                                <Typography component={"span"} color={theme.palette.secondary[800]}>
                                    {t(`common::msg::No search results found.`)}
                                </Typography>
                            </Grid>
                        }
                        maxFields={5}
                    />
                );
            } else if (type === "single_project") {
                return (
                    <ProjectSingleInput
                        data={values[id]}
                        disabled={false}
                        isPopperComponent={true}
                        noOptionsText={
                            <Grid
                                container
                                justifyContent={"center"}
                                alignItems={`center`}
                                flexDirection={"column"}
                                sx={{ height: "200px" }}
                            >
                                <ImageEmptySearchMini width={24} height={24} />
                                <Typography component={"span"} color={theme.palette.secondary[800]}>
                                    {t(`common::msg::No search results found.`)}
                                </Typography>
                            </Grid>
                        }
                        onChange={(project) => {
                            onHandleChangeValue(id, project);
                        }}
                    />
                );
            }
        },
        [domain, type, optionValues, onHandleChangeValue, PopperComponent, editTempValue],
    );
    const onHandleClickSubmit = useCallback(
        ({ id }, value) => {
            // date Type
            const filterOfDateType = decodedSearchParams.find((el) => el.type === "assetDateRange" && id === el.id);
            const filterOfUserType = decodedSearchParams.find((el) => ["users"].includes(el.type) && id === el.id);
            const filterOfCategoriesType = decodedSearchParams.find((el) => el.type === "categories" && id === el.id);
            if (filterOfDateType) {
                const startAt = convertToUTCDateTime(editTempValue.startAt, "start", true);
                const endAt = convertToUTCDateTime(editTempValue.endAt, "end", true);

                onClickSubmit({
                    startAt,
                    endAt,
                });
            } else if (filterOfUserType) {
                onClickSubmit({ [id]: value, [`${id}Name`]: editTempValue[`${id}Name`] });
            } else if (filterOfCategoriesType) {
                onClickSubmit({ [id]: value, categoryGroup: editTempValue.categoryGroup });
            } else {
                onClickSubmit({ [id]: value });
            }
        },
        [decodedSearchParams, editTempValue, onClickSubmit],
    );

    useEffect(() => {
        const decodedParams = values;
        setEditTempValue(values ?? {});
        const paramsForChips = parseSearchParamsToSearchValue(decodedParams).filter(
            (el) => el && !hiddenParams?.includes(el?.id),
        );
        setDecodedSearchParams(paramsForChips);
    }, [values, stage, assets.isActiveView]);

    const getMaxCount = useCallback(
        (id, value) => {
            if (!value || !Array.isArray(value) || value.length === 0) return null;
            if (optionValues?.[id]?.length < MAX_CONTENT_FIELDS) return null; //Note: 옵션 갯수가 최대 선택 가능 갯수보다 적으면 표시하지 않음
            return (
                <>
                    (
                    <Typography component="span" color={theme.palette.primary.main} display="inline">
                        {value.length}
                    </Typography>
                    /{MAX_CONTENT_FIELDS})
                </>
            );
        },
        [optionValues],
    );

    return (
        <Grid container flexDirection={"row"} gap={1.5}>
            {decodedSearchParams
                .filter((el) => !!el)
                .map((item, index) => {
                    return (
                        <SearchChipExpand
                            key={item.id ?? index}
                            item={item}
                            isDisabled={getChipIsChanged(item) || !!getValidationErrors(item)}
                            onDelete={() => {
                                onHandleDeleteChip(item);
                            }}
                            onCancel={() => {
                                onHandleCancel(item);
                            }}
                            onSave={() => {
                                onHandleClickSubmit(item, editTempValue[item.id]);
                            }}
                            component={getValueByParam(editTempValue, item)}
                            maxCount={getMaxCount(item.id, editTempValue[item.id] ?? [])}
                        />
                    );
                })}
            {Object.values(editTempValue).length > 0 && (
                <>
                    <Divider flexItem orientation="vertical" sx={{ my: 1 }} />
                    <TooltipButton
                        circled
                        disabled={!decodedSearchParams.length}
                        title={t("common::label::Clear", "Clear")}
                        buttonIcon={<IconReset size={16} />}
                        color={"greySecondary"}
                        onClickButton={onClickReset}
                    ></TooltipButton>
                </>
            )}
        </Grid>
    );
};

export default SearchFilters;
